import {
  type ReactNode,
  useCallback,
  useEffect,
  useState,
} from 'react';
import { useTranslation } from 'react-i18next';
import { useLocation } from 'react-router-dom';
import { useResponsiveTier } from 'pi-kiosk-shared/responsive';
import { tv } from 'tailwind-variants';
import { resolveAdminScreenTone } from './resolveAdminScreenTone';

/**
 * Viewport-locked app chrome: sidebar stays full height; only `<main>` scrolls.
 * `min-h-dvh` alone lets the document grow and exposes empty white below the rail.
 *
 * G2/G4: `<main>` uses `.enterprise-shell-main` — fluid `max-width: var(--admin-content-max-width)`
 * (`--container-full`) and equal L/R insets from `--admin-content-inset-*` (see
 * `admin.extensions.css`). Forms stay on `--container-form` via FormShell. Content region
 * mounts `.admin-shell-grid` (12/8/4). Vertical Spec rhythm is owned by ListPageLayout /
 * DataTableShell (`--space-*`), not main padding.
 */
const shell = tv({
  base: 'flex h-dvh w-full bg-[var(--color-an-bg)] text-[var(--color-an-text)]',
});

const sidebarRail = tv({
  base: [
    'group/rail relative h-full min-h-0 shrink-0 self-stretch',
    'max-lg:w-16 max-lg:overflow-visible max-lg:transition-[width] max-lg:duration-200',
    'max-lg:data-[expanded=true]:w-64',
    'lg:contents',
    'max-lg:[&_[data-testid=enterprise-sidebar]]:!flex',
    'max-lg:[&_[data-testid=enterprise-sidebar]]:h-full',
    'max-lg:[&_[data-testid=enterprise-sidebar]]:w-16 max-lg:[&_[data-testid=enterprise-sidebar]]:min-w-16',
    'max-lg:[&_[data-testid=enterprise-sidebar]]:overflow-hidden max-lg:[&_[data-testid=enterprise-sidebar]]:transition-[width]',
    'max-lg:data-[expanded=true]:[&_[data-testid=enterprise-sidebar]]:w-64',
    'max-lg:[&_[data-testid=enterprise-sidebar]>div:first-child]:max-h-0',
    'max-lg:[&_[data-testid=enterprise-sidebar]>div:first-child]:overflow-hidden',
    'max-lg:[&_[data-testid=enterprise-sidebar]>div:first-child]:border-0',
    'max-lg:[&_[data-testid=enterprise-sidebar]>div:first-child]:opacity-0',
    'max-lg:data-[expanded=true]:[&_[data-testid=enterprise-sidebar]>div:first-child]:max-h-none',
    'max-lg:data-[expanded=true]:[&_[data-testid=enterprise-sidebar]>div:first-child]:border-b',
    'max-lg:data-[expanded=true]:[&_[data-testid=enterprise-sidebar]>div:first-child]:border-white/10',
    'max-lg:data-[expanded=true]:[&_[data-testid=enterprise-sidebar]>div:first-child]:px-4',
    'max-lg:data-[expanded=true]:[&_[data-testid=enterprise-sidebar]>div:first-child]:py-5',
    'max-lg:data-[expanded=true]:[&_[data-testid=enterprise-sidebar]>div:first-child]:opacity-100',
    'max-lg:[&_[data-testid=enterprise-sidebar]_p]:hidden',
    'max-lg:[&_[data-testid=enterprise-sidebar]_span.truncate]:hidden',
    'max-lg:data-[expanded=true]:[&_[data-testid=enterprise-sidebar]_p]:block',
    'max-lg:data-[expanded=true]:[&_[data-testid=enterprise-sidebar]_span.truncate]:inline',
    'max-lg:[&_[data-testid=enterprise-sidebar]_button]:justify-center max-lg:[&_[data-testid=enterprise-sidebar]_button]:px-2',
    'max-lg:data-[expanded=true]:[&_[data-testid=enterprise-sidebar]_button]:justify-start',
    'max-lg:data-[expanded=true]:[&_[data-testid=enterprise-sidebar]_button]:px-3',
  ].join(' '),
});

const railToggle = tv({
  base: [
    'absolute top-4 -right-3 z-[var(--z-sticky)] flex size-7 items-center justify-center rounded-full',
    'border border-white/10 bg-[var(--color-rail-sidebar-bg)] text-[var(--color-rail-sidebar-text)]',
    'text-xs shadow-[var(--elevation-2)] lg:hidden',
  ].join(' '),
});

export interface EnterpriseShellProps {
  readonly sidebar: ReactNode;
  readonly header: ReactNode;
  readonly children: ReactNode;
  readonly drawer?: ReactNode;
  readonly bottomNav?: ReactNode;
  readonly moreDrawer?: ReactNode;
  readonly className?: string;
}

export function EnterpriseShell({
  sidebar,
  header,
  children,
  drawer,
  bottomNav,
  moreDrawer,
  className,
}: EnterpriseShellProps): JSX.Element {
  const { t } = useTranslation('admin');
  const location = useLocation();
  const adminTone = resolveAdminScreenTone(location.pathname);
  const tier = useResponsiveTier();
  const isCompact = tier === 'compact';
  const [railExpandedIntent, setRailExpandedIntent] = useState(() => false);
  const railExpanded = !isCompact && railExpandedIntent;

  useEffect(() => {
    const mq = window.matchMedia('(hover: hover) and (pointer: fine)');
    const sync = (): void => {
      if (!mq.matches) {
        setRailExpandedIntent(false);
      }
    };
    sync();
    mq.addEventListener('change', sync);
    return () => {
      mq.removeEventListener('change', sync);
    };
  }, []);

  useEffect(() => {
    const root = document.documentElement;
    const vv = window.visualViewport;
    const updateKeyboardInset = (): void => {
      if (vv === null || vv === undefined) {
        root.style.setProperty('--keyboard-inset', '0px');
        root.removeAttribute('data-keyboard-open');
        return;
      }
      const inset = Math.max(0, window.innerHeight - vv.height - vv.offsetTop);
      root.style.setProperty('--keyboard-inset', `${String(inset)}px`);
      if (inset > 0) {
        root.setAttribute('data-keyboard-open', 'true');
      } else {
        root.removeAttribute('data-keyboard-open');
      }
    };
    updateKeyboardInset();
    vv?.addEventListener('resize', updateKeyboardInset);
    vv?.addEventListener('scroll', updateKeyboardInset);
    return () => {
      vv?.removeEventListener('resize', updateKeyboardInset);
      vv?.removeEventListener('scroll', updateKeyboardInset);
      root.removeAttribute('data-keyboard-open');
    };
  }, []);

  // Lock document scroll while the shell owns the viewport (avoids white overscroll gap).
  useEffect(() => {
    const html = document.documentElement;
    const { body } = document;
    const previousHtmlOverflow = html.style.overflow;
    const previousBodyOverflow = body.style.overflow;
    html.style.overflow = 'hidden';
    body.style.overflow = 'hidden';
    return () => {
      html.style.overflow = previousHtmlOverflow;
      body.style.overflow = previousBodyOverflow;
    };
  }, []);

  const toggleRail = useCallback((): void => {
    setRailExpandedIntent((previous) => !previous);
  }, []);

  const showSidebar = !isCompact;

  return (
    <div
      className={shell({ className })}
      data-testid="enterprise-shell"
      data-admin-tone={adminTone}
    >
      <a
        href="#main"
        className="sr-only focus:not-sr-only focus:absolute focus:left-2 focus:top-2 focus:z-[var(--z-toast)] focus:rounded-md focus:bg-[var(--color-an-surface)] focus:px-3 focus:py-2 focus:text-sm focus:font-medium focus:text-[var(--color-an-text)] focus:shadow-[var(--elevation-2)] focus:outline focus:outline-2 focus:outline-offset-2 focus:outline-[var(--color-an-focus-ring)]"
      >
        {t('layout.skipToMain')}
      </a>
      {showSidebar ? (
        <div
          className={sidebarRail()}
          data-sidebar-mode={railExpanded ? 'expanded' : 'rail'}
          data-expanded={railExpanded ? 'true' : 'false'}
        >
          <button
            type="button"
            className={railToggle()}
            aria-label={
              railExpanded
                ? t('layout.enterpriseShell.collapseSidebar')
                : t('layout.enterpriseShell.expandSidebar')
            }
            aria-expanded={railExpanded}
            onClick={toggleRail}
          >
            {railExpanded ? '‹' : '›'}
          </button>
          {sidebar}
        </div>
      ) : null}
      <div
        className="flex min-h-0 min-w-0 flex-1 flex-col overflow-hidden"
        data-testid="enterprise-shell-content-column"
        style={{
          paddingBottom: isCompact
            ? 'calc(var(--admin-bottom-chrome, 0px) + var(--keyboard-inset, 0px))'
            : undefined,
        }}
      >
        {header}
        <main
          id="main"
          className="enterprise-shell-main min-h-0 flex-1 overflow-auto"
        >
          <div
            id="admin-export-surface"
            className="admin-shell-grid"
            data-testid="admin-shell-grid"
            data-admin-export-surface="page"
          >
            {children}
          </div>
        </main>
      </div>
      {drawer}
      {isCompact ? bottomNav : null}
      {isCompact ? moreDrawer : null}
    </div>
  );
}
